import { Box, Button, Drawer, Paper, TextField, Toolbar, useTheme, Typography, Select, MenuItem, ListItemText, Checkbox, SelectChangeEvent, OutlinedInput, FormControl, InputLabel } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Environment } from 'shared/environment';
import { useIsDrawerOpen, useIsDrawerMenuOpen } from 'shared/store/drawer';
import { enUS, ptBR } from 'date-fns/locale';
import env from 'config/env';
import { dateToLocalISO, IKeyValue, stringToLocalISO } from 'shared/utils';


export interface IListaFiltros {
  tipo: 'SelectMultiple' | 'Select' | 'TextField' | 'TextFieldSigla' |'TextFieldNumeric' | 'DateTimePicker';
  mapValue: IKeyValue[];
  label: string;
  textoBusca: string | string[];
  handleTextoBusca?: (novoTexto: string | string[]) => void;
}
interface IFerramentasDaListagemProps {
  listaFiltros: IListaFiltros[];
  handlePesquisar?: () => void;
  handleLimpar?: () => void;
}

export const FerramentasDaListagem: React.FC<IFerramentasDaListagemProps> = ({
  listaFiltros,
  handlePesquisar,
  handleLimpar,
}) => {
  const theme = useTheme();
  const isDrawerOpen = useIsDrawerOpen();
  const isDrawerMenuOpen = useIsDrawerMenuOpen();
  const { t } = useTranslation();

  //console.log('listaFiltros',listaFiltros);

  const localeSel = (env.language === 'en' ? enUS : ptBR);
  const localeMask = (env.language === 'en' ? '__/__/____ __:__:__ _M' : '__/__/____ __:__:__');
  const localeInputFormat = (env.language === 'en' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss');

  const handleChange = (event: SelectChangeEvent<string[]>, item: IListaFiltros) => {
    const {
      target: { value },
    } = event;
    item.handleTextoBusca?.(typeof value === 'string' ? value.split(',') : value);
    //console.log('valor enviado',typeof value === 'string' ? value.split(',') : value);
  };


  return (
    <Box
      component={Drawer}
      sx={{
        width: isDrawerMenuOpen?Environment.TAMANHO_DO_DRAWER_LIST + Environment.TAMANHO_DO_DRAWER_MENU:Environment.TAMANHO_DO_DRAWER_LIST,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isDrawerMenuOpen?Environment.TAMANHO_DO_DRAWER_LIST + Environment.TAMANHO_DO_DRAWER_MENU:Environment.TAMANHO_DO_DRAWER_LIST,
          boxSizing: 'border-box',
        },
      }}
      variant="persistent"
      anchor="left"
      open={isDrawerOpen}>
      <Toolbar sx={{
        height: theme.spacing(20),
      }} />
      <Box marginLeft={`${isDrawerMenuOpen?Environment.TAMANHO_DO_DRAWER_MENU:0}px`} height="100%" display="flex" flexDirection="column" component={Paper}>
        <Box p={4} component={Paper}  >
          <Typography
            overflow="hidden"
            whiteSpace="nowrap"
            textOverflow="ellipses"
            variant={'h5'}
            align='center'
          >
            {t('components.ferramentasDaListagem.filtros')}
          </Typography>
        </Box>

        <Box flex={1} p={4}>
          {listaFiltros.map((itemFiltro) => (
            <FormControl key={itemFiltro.label} margin='dense' sx={{ width: '100%' }}>
              {itemFiltro.tipo === 'SelectMultiple' && (
                <>
                  <InputLabel id={`label-select-${itemFiltro.label}`}>{itemFiltro.label}</InputLabel>
                  <Select
                    size='small'
                    labelId={`label-select-${itemFiltro.label}`}
                    id={`select-${itemFiltro.label}`}
                    multiple
                    input={<OutlinedInput label={itemFiltro.label} />}
                    value={itemFiltro.textoBusca as string[]}
                    onChange={(e) => handleChange(e, itemFiltro)}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {itemFiltro.mapValue.map((item) => (
                      <MenuItem key={item.key} value={item.key}>
                        <Checkbox checked={itemFiltro.textoBusca.indexOf(item.key) > -1} />
                        <ListItemText primary={`${item.key} - ${item.value}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
              {itemFiltro.tipo === 'Select' && (
                <>
                  <InputLabel id={`label-select-${itemFiltro.label}`}>{itemFiltro.label}</InputLabel>
                  <Select
                    size='small'
                    labelId={`label-select-${itemFiltro.label}`}
                    id={`select-${itemFiltro.label}`}
                    input={<OutlinedInput label={itemFiltro.label} />}
                    value={itemFiltro.textoBusca as string[]}
                    onChange={(e) => handleChange(e, itemFiltro)}
                  >
                    <MenuItem value=''>
                      <ListItemText primary={t('components.ferramentasDaListagem.todos')} />
                    </MenuItem>
                    {itemFiltro.mapValue.map((item) => (
                      <MenuItem key={item.key} value={item.key}>
                        <ListItemText primary={item.value} />
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}
              {itemFiltro.tipo === 'TextField' && (
                <TextField
                  size='small'
                  type='text'
                  label={itemFiltro.label}
                  value={itemFiltro.textoBusca as string[]}
                  onChange={(e) => itemFiltro.handleTextoBusca?.(e.target.value)}
                />
              )}
              {itemFiltro.tipo === 'TextFieldSigla' && (
                <TextField
                  inputProps={{maxLength:3}}
                  size='small'
                  type='text'
                  label={itemFiltro.label}
                  value={itemFiltro.textoBusca as string[]}
                  onChange={(e) => itemFiltro.handleTextoBusca?.(e.target.value)}
                />
              )}
              {itemFiltro.tipo === 'TextFieldNumeric' && (
                <TextField
                  size='small'
                  type='number'
                  label={itemFiltro.label}
                  value={itemFiltro.textoBusca as string[]}
                  onChange={(e) => itemFiltro.handleTextoBusca?.(e.target.value)}
                />
              )}
              {itemFiltro.tipo === 'DateTimePicker' && (
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeSel}>
                  <DateTimePicker
                    label={itemFiltro.label}
                    value={itemFiltro.textoBusca.toString()}
                    inputFormat={localeInputFormat}
                    mask={localeMask}
                    views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                    onChange={(newValue, keyBoardValue) => { keyBoardValue?.length === 19 && newValue !== null && itemFiltro.handleTextoBusca?.(stringToLocalISO(newValue)); }}
                    onAccept={(newValue) => itemFiltro.handleTextoBusca?.((newValue === null) ? '' : dateToLocalISO(new Date(newValue)))}
                    renderInput={(params) => <TextField size='small' {...params} />}
                  />
                </LocalizationProvider>
              )}

            </FormControl>
          ))}
        </Box>

        <Box display="flex" p={4} >
          <Button
            color='primary'
            disableElevation
            variant='contained'
            onClick={handlePesquisar}
            sx={{ marginX: theme.spacing(1) }}
          >
            {t('components.ferramentasDaListagem.pesquisar')}
          </Button>
          <Button
            color='primary'
            disableElevation
            variant='contained'
            onClick={handleLimpar}
            sx={{ marginX: theme.spacing(1) }}
          >
            {t('components.ferramentasDaListagem.limpar')}
          </Button>
        </Box>
      </Box>
    </Box >
  );
};
